import { lazy, Suspense } from "react";
import { BrowserRouter, MemoryRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { LazyMotion, domAnimation } from "framer-motion";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import Index from "./pages/Index";

// Lazy-load all secondary pages to keep the initial bundle small and fast
const Imprint = lazy(() => import("./pages/Imprint"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RightOfWithdrawal = lazy(() => import("./pages/RightOfWithdrawal"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const DataExport = lazy(() => import("./pages/DataExport"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Updates = lazy(() => import("./pages/Updates"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FeaturesOverview = lazy(() => import("./pages/FeaturesOverview"));
const FeaturePage = lazy(() => import("./pages/FeaturePage"));
const PremiumFeaturesOverview = lazy(() => import("./pages/PremiumFeaturesOverview"));
const PremiumFeaturePage = lazy(() => import("./pages/PremiumFeaturePage"));
const AppDeepLink = lazy(() => import("./pages/AppDeepLink"));
const NotFound = lazy(() => import("./pages/NotFound"));

const ScrollToTop = () => {
  useScrollToTop();
  return null;
};

const App = ({ initialUrl }: { initialUrl?: string } = {}) => {
  const englishLegalPath = /^\/(imprint|privacy-policy|terms-of-service|terms-and-conditions|right-of-withdrawal|delete-account|data-export)$/.test(initialUrl || "");
  const routedApp = (
    <>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                {/* English & standard routes */}
                <Route path="/" element={<Index />} />
                <Route path="/en" element={<Index />} />
                <Route path="/en/" element={<Index />} />
                <Route path="/imprint" element={<Imprint targetLang="en" />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy targetLang="en" />} />
                <Route path="/terms-of-service" element={<TermsOfService targetLang="en" />} />
                <Route path="/terms-and-conditions" element={<TermsOfService targetLang="en" />} />
                <Route
                  path="/right-of-withdrawal"
                  element={<RightOfWithdrawal targetLang="en" />}
                />
                <Route path="/delete-account" element={<DeleteAccount targetLang="en" />} />
                <Route path="/data-export" element={<DataExport targetLang="en" />} />

                {/* German route aliases */}
                <Route path="/impressum" element={<Imprint targetLang="de" />} />
                <Route path="/datenschutz" element={<PrivacyPolicy targetLang="de" />} />
                <Route path="/datenschutzerklaerung" element={<PrivacyPolicy targetLang="de" />} />
                <Route path="/nutzungsbedingungen" element={<TermsOfService targetLang="de" />} />
                <Route
                  path="/widerruf"
                  element={<RightOfWithdrawal targetLang="de" />}
                />
                <Route path="/konto-loeschen" element={<DeleteAccount targetLang="de" />} />
                <Route path="/datenexport" element={<DataExport targetLang="de" />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="/updates" element={<Updates />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/features" element={<FeaturesOverview />} />
                <Route path="/features/:slug" element={<FeaturePage />} />
                <Route
                  path="/premium/features"
                  element={<PremiumFeaturesOverview />}
                />
                <Route
                  path="/premium/features/:slug"
                  element={<PremiumFeaturePage />}
                />
                {/* Deep-link entry point for Android App Links – no web UI */}
                <Route path="/app" element={<AppDeepLink />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
    </>
  );

  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <LanguageProvider initialLanguage={initialUrl?.startsWith("/en") || englishLegalPath ? "en" : undefined}>
          <LazyMotion features={domAnimation}>
            {initialUrl ? <MemoryRouter initialEntries={[initialUrl]}>{routedApp}</MemoryRouter> : <BrowserRouter>{routedApp}</BrowserRouter>}
          </LazyMotion>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
